'use client';

import React, { useState, useMemo, useEffect } from 'react';
import InteractivePopover from '@/components/common/InteractivePopover';
import { OptimizedImage } from '@/components/common/OptimizedImage';
import FullscreenImageViewer from '@/components/common/FullscreenImageViewer';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, A11y, Autoplay } from 'swiper/modules';
import type { Hotel } from '@/components/packages/Hotels';
import type { Vehicle } from '@/components/packages/Vehicles';
import { fetchRoomMedia } from '@/utils/fetchStayExperienceMedia';
import type { NormalizedRoomOption } from '@/utils/roomOptionHelpers';
import { formatRoomOptionsLabel } from '@/utils/roomOptionHelpers';
import {
  typography,
  backgroundColors,
  typographyColors,
  brandColors,
  borders,
  shadows,
  spacing,
} from '@/design-system/utils/tokens';
import { Info } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/pagination';

/** Resolved transfer for display: pickup/dropoff names and duration, plus optional vehicle */
export interface TransferDisplayItem {
  fromName?: string;
  toName?: string;
  duration?: string;
  vehicle?: Vehicle;
}

/** Hotel-like object from a populated itinerary item */
export type HotelFromItinerary = {
  _id?: string;
  name?: string;
  type?: string;
  location?: string;
  address?: string;
  description?: string;
  imageUrl?: string[];
  media?: Array<{ url?: string; format?: string }>;
  amenities?: string[] | unknown[];
};

/** Transfer from a populated itinerary item (displayed as-is, no lookup) */
export type TransferFromItinerary = {
  _id: string;
  pickUpPoint: string;
  dropOffPoint: string;
  type: string;
  duration?: string;
  vehicleId?: string;
};

interface ItineraryInfoPopoverProps {
  hotels?: Hotel[];
  vehicles?: Vehicle[];
  /** When provided, transfer tab shows one card per item with pickup, dropoff, duration + vehicle details */
  transfersForDay?: TransferDisplayItem[];
  /** Room for this day from itinerary; when present, show room name and media from room-option API */
  roomId?: { _id: string; name?: string; description?: string };
  /** When provided, use this hotel from the itinerary item directly (no lookup) */
  hotelFromItinerary?: HotelFromItinerary;
  /** When provided, use this room from the itinerary item directly */
  roomFromItinerary?: { _id: string; name?: string; description?: string };
  roomOptionsFromItinerary?: NormalizedRoomOption[];
  /** When provided, use this transfer from the itinerary item directly (pickup/dropoff/duration from object) */
  transferFromItinerary?: TransferFromItinerary;
  /** Optional custom trigger; defaults to info icon */
  trigger?: React.ReactNode;
  triggerClassName?: string;
}

function getHotelImages(hotel: Hotel | HotelFromItinerary): string[] {
  const urls: string[] = [];
  if (hotel.media && Array.isArray(hotel.media)) {
    hotel.media
      .filter(
        (item: { format?: string; url?: string }) =>
          item.format === 'image' && item.url
      )
      .forEach((item: { url?: string }) => urls.push(item.url!));
  }
  if (hotel.imageUrl && Array.isArray(hotel.imageUrl)) {
    hotel.imageUrl.forEach(url => {
      if (url && typeof url === 'string' && !urls.includes(url)) {
        urls.push(url);
      }
    });
  }
  return urls.length > 0 ? urls : ['/assets/images/No-Image.jpg'];
}

/** Deduplicate by _id or stable key to avoid showing same hotel/vehicle twice */
function dedupeHotels(list: Hotel[]): Hotel[] {
  const seen = new Set<string>();
  return list.filter(h => {
    const key =
      (h as Hotel & { _id?: string })._id ??
      `${h.name ?? ''}|${h.location ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function dedupeVehicles(list: Vehicle[]): Vehicle[] {
  const seen = new Set<string>();
  return list.filter(v => {
    const key = v._id ?? v.title ?? '';
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Format duration value as hours (e.g. "2" -> "2 hours", "1.5" -> "1.5 hours") */
function formatDurationHours(duration: string | undefined): string {
  if (duration == null || String(duration).trim() === '') return '';
  const num = parseFloat(String(duration).trim());
  if (Number.isNaN(num)) return duration;
  return num === 1 ? '1 hour' : `${num} hours`;
}

/** Common room amenities shown as tags in the Room tab */
const COMMON_ROOM_AMENITIES = [
  'WiFi',
  'AC',
  'Heater',
  'TV',
  'Mini Bar',
  'Safe',
  'Room Service',
];

const ItineraryInfoPopover: React.FC<ItineraryInfoPopoverProps> = ({
  hotels = [],
  vehicles = [],
  transfersForDay = [],
  roomId,
  hotelFromItinerary,
  roomFromItinerary,
  roomOptionsFromItinerary = [],
  transferFromItinerary,
  trigger,
  triggerClassName,
}) => {
  const effectiveRoom = roomFromItinerary ?? roomId;
  const displayRoomOptions =
    roomOptionsFromItinerary.length > 0
      ? roomOptionsFromItinerary
      : effectiveRoom?.name
        ? [
            {
              id: effectiveRoom._id || effectiveRoom.name,
              name: effectiveRoom.name,
              description: effectiveRoom.description || '',
              imageUrl: [] as string[],
            },
          ]
        : [];
  const roomDisplayLabel =
    displayRoomOptions.length > 1
      ? formatRoomOptionsLabel(displayRoomOptions)
      : displayRoomOptions[0]?.name;

  const uniqueHotels = useMemo(() => {
    if (hotelFromItinerary && typeof hotelFromItinerary === 'object') {
      return [hotelFromItinerary as Hotel];
    }
    return dedupeHotels(hotels);
  }, [hotels, hotelFromItinerary]);

  const uniqueVehicles = useMemo(() => dedupeVehicles(vehicles), [vehicles]);
  const vehicleById = useMemo(() => {
    const map: Record<string, Vehicle> = {};
    vehicles.forEach(v => {
      if (v._id) map[v._id] = v;
    });
    return map;
  }, [vehicles]);

  const transferItems = useMemo(() => {
    if (transfersForDay.length > 0) return transfersForDay;
    if (transferFromItinerary) {
      const vehicle = transferFromItinerary.vehicleId
        ? vehicleById[transferFromItinerary.vehicleId]
        : undefined;
      return [
        {
          fromName: transferFromItinerary.pickUpPoint,
          toName: transferFromItinerary.dropOffPoint,
          duration: transferFromItinerary.duration,
          vehicle,
        } as TransferDisplayItem,
      ];
    }
    return uniqueVehicles.map(vehicle => ({ vehicle }));
  }, [transferFromItinerary, transfersForDay, uniqueVehicles, vehicleById]);
  const hasTransferDisplay =
    transfersForDay.length > 0 || Boolean(transferFromItinerary);
  const hasHotels = uniqueHotels.length > 0;
  const hasVehicles = uniqueVehicles.length > 0 || hasTransferDisplay;
  const hasRoomContent = displayRoomOptions.length > 0;
  /** Show tab bar whenever there is any content (single or multiple tabs). */
  const showTabs = hasHotels || hasVehicles || hasRoomContent;
  const showThreeTabs = hasHotels && hasVehicles;

  const [activeTab, setActiveTab] = useState<'hotel' | 'room' | 'transfer'>(
    'hotel'
  );
  const [roomMediaUrls, setRoomMediaUrls] = useState<string[]>([]);
  const [roomLightboxOpen, setRoomLightboxOpen] = useState(false);
  const [roomLightboxIndex] = useState(0);

  useEffect(() => {
    const primaryRoom = displayRoomOptions[0];
    const inlineImages = displayRoomOptions.flatMap(room => room.imageUrl);

    if (inlineImages.length > 0) {
      setRoomMediaUrls(inlineImages);
      return;
    }

    if (!primaryRoom?.id) {
      setRoomMediaUrls([]);
      return;
    }

    fetchRoomMedia(primaryRoom.id)
      .then(items => setRoomMediaUrls(items.map(m => m.url).filter(Boolean)))
      .catch(() => setRoomMediaUrls([]));
  }, [displayRoomOptions]);

  const effectiveTab = (() => {
    if (!showTabs) {
      return hasHotels ? 'hotel' : hasVehicles ? 'transfer' : 'hotel';
    }
    if (activeTab === 'transfer' && !hasVehicles) {
      return hasRoomContent ? 'room' : 'hotel';
    }
    if (activeTab === 'room' && !showThreeTabs && !hasRoomContent) {
      return 'hotel';
    }
    return activeTab;
  })();

  const hotelImagesMap = useMemo(() => {
    const map: Record<number, string[]> = {};
    uniqueHotels.forEach((h, i) => {
      map[i] = getHotelImages(h);
    });
    return map;
  }, [uniqueHotels]);

  const imagesForActiveTab = useMemo(() => {
    if (effectiveTab === 'hotel') {
      if (!hasHotels) return ['/assets/images/No-Image.jpg'];
      return uniqueHotels.flatMap(
        (h, i) => hotelImagesMap[i] ?? getHotelImages(h)
      );
    }
    if (effectiveTab === 'room') {
      if (roomMediaUrls.length > 0) return roomMediaUrls;
      if (hasHotels) {
        return uniqueHotels.flatMap(
          (h, i) => hotelImagesMap[i] ?? getHotelImages(h)
        );
      }
      return ['/assets/images/No-Image.jpg'];
    }
    if (effectiveTab === 'transfer') {
      if (!hasVehicles) return ['/assets/images/No-Image.jpg'];
      const vehiclesForCarousel = hasTransferDisplay
        ? transferItems
            .map(t => t.vehicle)
            .filter((v): v is Vehicle => v != null)
        : uniqueVehicles;
      const urls = vehiclesForCarousel.flatMap(v =>
        (v.imageUrl || []).filter(Boolean)
      );
      return urls.length > 0 ? urls : ['/assets/images/No-Image.jpg'];
    }
    return ['/assets/images/No-Image.jpg'];
  }, [
    effectiveTab,
    uniqueHotels,
    uniqueVehicles,
    hasHotels,
    hasVehicles,
    hotelImagesMap,
    hasTransferDisplay,
    transferItems,
    roomMediaUrls,
  ]);

  const carouselImages =
    imagesForActiveTab.length > 0
      ? imagesForActiveTab
      : ['/assets/images/No-Image.jpg'];

  const showPopover = hasHotels || hasVehicles;
  if (!showPopover) return null;

  const tabButtonStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: `${spacing[2]} ${spacing[4]}`,
    fontSize: typography.size.base,
    fontWeight: typography.weight.medium,
    fontFamily: typography.fonts.primary,
    lineHeight: typography.lineHeight.normal,
    color: active ? typographyColors.primary : typographyColors.secondary,
    borderBottom: active
      ? `2px solid ${brandColors.primary}`
      : '2px solid transparent',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'color 0.15s, border-color 0.15s',
  });

  const popoverShellStyle: React.CSSProperties = {
    borderRadius: borders.radius.xl,
    border: `1px solid ${backgroundColors.shade}`,
    boxShadow: shadows.md,
    backgroundColor: backgroundColors.primary,
  };

  return (
    <>
      <InteractivePopover
        triggerClassName={
          triggerClassName ??
          'cursor-pointer flex-shrink-0 inline-flex items-center justify-center w-8 h-8 text-yellow-500 hover:text-yellow-600'
        }
        contentClassName='w-[320px] p-0 overflow-hidden'
        align='start'
        side='bottom'
        trigger={
          trigger ?? (
            <span
              className='inline-flex text-yellow-500'
              aria-label='Itinerary info'
            >
              <Info className='w-5 h-5' strokeWidth={2} />
            </span>
          )
        }
        content={
          <div
            style={{
              ...popoverShellStyle,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: 480,
            }}
            onClick={e => e.stopPropagation()}
            onPointerDown={e => e.stopPropagation()}
          >
            {/* Tabs: Hotel, Room, Transfer (show tab title even for single tab) */}
            {showTabs && (
              <div
                role='tablist'
                style={{
                  display: 'flex',
                  borderBottom: `1px solid ${backgroundColors.shade}`,
                  flexShrink: 0,
                  backgroundColor: backgroundColors.primary,
                }}
                onClick={e => e.stopPropagation()}
              >
                {hasHotels && (
                  <button
                    type='button'
                    onClick={e => {
                      e.stopPropagation();
                      setActiveTab('hotel');
                    }}
                    style={tabButtonStyle(effectiveTab === 'hotel')}
                  >
                    Hotel
                  </button>
                )}
                {hasRoomContent && (
                  <button
                    type='button'
                    onClick={e => {
                      e.stopPropagation();
                      setActiveTab('room');
                    }}
                    style={tabButtonStyle(effectiveTab === 'room')}
                  >
                    Room
                  </button>
                )}
                {hasVehicles && (
                  <button
                    type='button'
                    onClick={e => {
                      e.stopPropagation();
                      setActiveTab('transfer');
                    }}
                    style={tabButtonStyle(effectiveTab === 'transfer')}
                  >
                    Transfer
                  </button>
                )}
              </div>
            )}

            <div
              className='relative overflow-hidden flex-shrink-0'
              style={{ paddingTop: showTabs ? spacing[3] : 0 }}
            >
              <div className='w-full h-[180px] relative overflow-hidden'>
                <Swiper
                  key={effectiveTab}
                  modules={[Pagination, A11y, Autoplay]}
                  slidesPerView={1}
                  pagination={{ clickable: true }}
                  autoplay={{
                    delay: 3000,
                    disableOnInteraction: false,
                  }}
                  className='w-full h-full'
                >
                  {carouselImages.map((src, idx) => (
                    <SwiperSlide key={`${effectiveTab}-${src}-${idx}`}>
                      <div className='relative w-full h-full'>
                        <OptimizedImage
                          src={src || '/assets/images/No-Image.jpg'}
                          alt={
                            effectiveTab === 'hotel'
                              ? `Hotel ${idx + 1}`
                              : effectiveTab === 'room'
                                ? `Room ${idx + 1}`
                                : `Vehicle ${idx + 1}`
                          }
                          fill
                          style={{ objectFit: 'cover' }}
                          priority={idx === 0}
                          loading={idx === 0 ? 'eager' : 'lazy'}
                          sizes='320px'
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>

            <div
              style={{
                padding: spacing[3],
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
              }}
            >
              {effectiveTab === 'hotel' && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing[4],
                  }}
                >
                  {hasHotels ? (
                    uniqueHotels.map((hotel, index) => (
                      <div
                        key={`${(hotel as Hotel & { _id?: string })._id ?? hotel.name}-${index}`}
                      >
                        <h4
                          style={{
                            fontSize: typography.size.base,
                            fontWeight: typography.weight.semibold,
                            color: typographyColors.primary,
                            fontFamily: typography.fonts.primary,
                            lineHeight: typography.lineHeight.normal,
                            marginBottom: spacing[1],
                          }}
                        >
                          {hotel.name}
                        </h4>
                        {hotel.type && (
                          <p
                            style={{
                              fontSize: typography.size.sm,
                              color: typographyColors.text,
                              textTransform: 'capitalize',
                              lineHeight: typography.lineHeight.relaxed,
                              marginBottom: 4,
                            }}
                          >
                            {hotel.type}
                          </p>
                        )}
                        {hotel.location && (
                          <p
                            style={{
                              fontSize: typography.size.sm,
                              color: typographyColors.text,
                              lineHeight: typography.lineHeight.relaxed,
                              marginBottom: 4,
                            }}
                          >
                            {hotel.location}
                          </p>
                        )}
                        {hotel.address && (
                          <p
                            style={{
                              fontSize: typography.size.sm,
                              color: typographyColors.text,
                              lineHeight: typography.lineHeight.relaxed,
                              marginBottom: 4,
                            }}
                          >
                            {hotel.address}
                          </p>
                        )}
                        {hotel.description && (
                          <p
                            style={{
                              fontSize: typography.size.sm,
                              color: typographyColors.text,
                              lineHeight: typography.lineHeight.relaxed,
                              marginBottom: spacing[2],
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {hotel.description}
                          </p>
                        )}
                        {hotel.amenities &&
                          hotel.amenities.length > 0 &&
                          (() => {
                            const amenityTags = hotel.amenities.flatMap(a =>
                              typeof a === 'string' && a.includes(',')
                                ? a
                                    .split(',')
                                    .map(s => s.trim())
                                    .filter(Boolean)
                                : [String(a).trim()].filter(Boolean)
                            );
                            const displayTags = amenityTags.slice(0, 8);
                            const remaining =
                              amenityTags.length - displayTags.length;
                            return (
                              <div
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 6,
                                }}
                              >
                                {displayTags.map((a, i) => (
                                  <span
                                    key={`${a}-${i}`}
                                    style={{
                                      fontSize: typography.size.xs,
                                      padding: '4px 8px',
                                      backgroundColor: backgroundColors.shade,
                                      color: typographyColors.text,
                                      borderRadius: borders.radius.xs,
                                      lineHeight: typography.lineHeight.normal,
                                    }}
                                  >
                                    {a}
                                  </span>
                                ))}
                                {remaining > 0 && (
                                  <span
                                    style={{
                                      fontSize: typography.size.xs,
                                      color: typographyColors.secondary,
                                      lineHeight: typography.lineHeight.normal,
                                    }}
                                  >
                                    +{remaining}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                      </div>
                    ))
                  ) : (
                    <p
                      style={{
                        fontSize: typography.size.base,
                        color: typographyColors.text,
                        padding: spacing[4],
                        lineHeight: typography.lineHeight.relaxed,
                      }}
                    >
                      No accommodation for this day.
                    </p>
                  )}
                </div>
              )}

              {effectiveTab === 'room' && displayRoomOptions.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing[3],
                  }}
                >
                  {displayRoomOptions.map((room, index) => (
                    <div
                      key={room.id || `${room.name}-${index}`}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: spacing[2],
                        paddingBottom:
                          index < displayRoomOptions.length - 1
                            ? spacing[3]
                            : 0,
                        borderBottom:
                          index < displayRoomOptions.length - 1
                            ? `1px solid ${backgroundColors.shade}`
                            : undefined,
                      }}
                    >
                      <div
                        style={{
                          fontSize: typography.size.sm,
                          fontWeight: typography.weight.medium,
                          color: typographyColors.primary,
                          lineHeight: typography.lineHeight.relaxed,
                        }}
                      >
                        {displayRoomOptions.length > 1
                          ? room.name
                          : `Room: ${room.name}`}
                      </div>
                      {room.description && (
                        <p
                          style={{
                            fontSize: typography.size.sm,
                            color: typographyColors.text,
                            lineHeight: typography.lineHeight.relaxed,
                          }}
                        >
                          {room.description}
                        </p>
                      )}
                    </div>
                  ))}
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                    }}
                  >
                    {COMMON_ROOM_AMENITIES.map((a, i) => (
                      <span
                        key={`room-amenity-${a}-${i}`}
                        style={{
                          fontSize: typography.size.xs,
                          padding: '4px 8px',
                          backgroundColor: backgroundColors.shade,
                          color: typographyColors.text,
                          borderRadius: borders.radius.xs,
                          lineHeight: typography.lineHeight.normal,
                        }}
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {effectiveTab === 'transfer' && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing[3],
                  }}
                >
                  {hasVehicles ? (
                    transferItems.map((item, index) => {
                      const v = item.vehicle;
                      const fromName =
                        'fromName' in item ? item.fromName : undefined;
                      const toName = 'toName' in item ? item.toName : undefined;
                      const durationLabel = formatDurationHours(
                        'duration' in item ? item.duration : undefined
                      );
                      const lineStyle: React.CSSProperties = {
                        fontSize: typography.size.sm,
                        color: typographyColors.text,
                        marginBottom: 2,
                      };
                      const key = v?._id ?? `transfer-${index}`;
                      return (
                        <div key={key}>
                          <div style={{ padding: spacing[2] }}>
                            {fromName && (
                              <p style={lineStyle}>
                                <span
                                  style={{
                                    fontWeight: typography.weight.medium,
                                    color: typographyColors.primary,
                                  }}
                                >
                                  Pickup:
                                </span>{' '}
                                {fromName}
                              </p>
                            )}
                            {toName && (
                              <p style={lineStyle}>
                                <span
                                  style={{
                                    fontWeight: typography.weight.medium,
                                    color: typographyColors.primary,
                                  }}
                                >
                                  Dropoff:
                                </span>{' '}
                                {toName}
                              </p>
                            )}
                            {durationLabel && (
                              <p style={lineStyle}>
                                <span
                                  style={{
                                    fontWeight: typography.weight.medium,
                                    color: typographyColors.primary,
                                  }}
                                >
                                  Duration:
                                </span>{' '}
                                {durationLabel}
                              </p>
                            )}
                            {v && (
                              <div
                                style={{
                                  marginTop: spacing[3],
                                  paddingTop: spacing[2],
                                  borderTop: `1px solid ${backgroundColors.shade}`,
                                }}
                              >
                                <h4
                                  style={{
                                    fontSize: typography.size.xs,
                                    fontWeight: typography.weight.semibold,
                                    color: typographyColors.secondary,
                                    fontFamily: typography.fonts.primary,
                                    lineHeight: typography.lineHeight.normal,
                                    marginBottom: spacing[2],
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.02em',
                                  }}
                                >
                                  Vehicle details
                                </h4>
                                <p style={lineStyle}>
                                  <span
                                    style={{
                                      fontWeight: typography.weight.medium,
                                      color: typographyColors.primary,
                                    }}
                                  >
                                    Vehicle name:
                                  </span>{' '}
                                  <span
                                    style={{
                                      fontWeight: typography.weight.semibold,
                                      color: typographyColors.primary,
                                    }}
                                  >
                                    {v.title}
                                  </span>
                                </p>
                                {v.type && (
                                  <p style={lineStyle}>Type: {v.type}</p>
                                )}
                                {v.seatingCapacity != null && (
                                  <p style={lineStyle}>
                                    Seats: {v.seatingCapacity}
                                  </p>
                                )}
                                {v.features && v.features.length > 0 && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 4,
                                      marginTop: 4,
                                    }}
                                  >
                                    {v.features.slice(0, 4).map((f, i) => (
                                      <span
                                        key={`${f}-${i}`}
                                        style={{
                                          fontSize: typography.size.xs,
                                          padding: '2px 6px',
                                          backgroundColor:
                                            backgroundColors.shade,
                                          color: typographyColors.text,
                                          borderRadius: borders.radius.xs,
                                        }}
                                      >
                                        {f}
                                      </span>
                                    ))}
                                    {v.features.length > 4 && (
                                      <span
                                        style={{
                                          fontSize: typography.size.xs,
                                          color: typographyColors.secondary,
                                        }}
                                      >
                                        +{v.features.length - 4}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p
                      style={{
                        fontSize: typography.size.base,
                        color: typographyColors.text,
                        padding: spacing[4],
                        lineHeight: typography.lineHeight.relaxed,
                      }}
                    >
                      No transfers for this day.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        }
      />
      <FullscreenImageViewer
        images={roomMediaUrls}
        isOpen={roomLightboxOpen}
        onClose={() => setRoomLightboxOpen(false)}
        initialIndex={roomLightboxIndex}
        title={roomDisplayLabel ?? 'Room'}
        autoRotateInterval={999999}
      />
    </>
  );
};

export default ItineraryInfoPopover;
